/**
 * @file 漫画业务常量
 * @description 统一维护漫画状态、漫画类别及其在 Element Plus 文本组件中的展示语义。
 */

/** Element Plus 文本状态色，仅允许业务配置使用组件支持的枚举值。 */
export type ElementTextType =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'

/** 漫画筛选项与展示项共用配置。 */
export interface MangaConfig {
  /** 后端存储的数字枚举值。 */
  value: number
  /** 页面展示给用户的中文文案。 */
  label: string
  /** Element Plus 文本组件的状态色。 */
  type: ElementTextType
}

/** 漫画连载状态配置，key 仅供前端代码按语义引用。 */
export const MANGA_STATUS_CONFIG: Record<string, MangaConfig> = {
  ONGOING: { value: 1, label: '正常连载中', type: 'success' },
  NEED_HELP: { value: 2, label: '多话未做需填坑', type: 'warning' },
  URGENTLY_NEED_PEOPLE: {
    value: 3,
    label: '现在立刻非常需要人手！',
    type: 'danger'
  },
  FINISHED: { value: 4, label: '已完结', type: 'primary' },
  ON_HOLD: { value: 5, label: '暂时搁置', type: 'info' },
  CANCELLED: { value: 6, label: '已腰斩', type: 'primary' },
  ABANDONED: { value: 7, label: '已弃坑', type: 'info' },
  AUTHOR_NOT_UPDATING: { value: 8, label: '作者不更新', type: 'info' }
}

/** 漫画类别配置，value 需要与后端枚举保持一致。 */
export const MANGA_CATEGORY_CONFIG: Record<string, MangaConfig> = {
  SERIAL: { value: 1, label: '长篇连载', type: 'success' },
  SHORT_COMIC: { value: 2, label: '短篇漫画', type: 'primary' },
  COLLECTION: { value: 3, label: '漫画合集', type: 'warning' },
  YURI_ANIMATION: { value: 4, label: '百合动画', type: 'info' }
}

/** 判断漫画类别是否为长篇连载。 */
export const isSerial = (category?: number): boolean =>
  category === MANGA_CATEGORY_CONFIG.SERIAL.value

/** 判断漫画类别是否为短篇漫画。 */
export const isShortComic = (category?: number): boolean =>
  category === MANGA_CATEGORY_CONFIG.SHORT_COMIC.value

/** 判断漫画类别是否为漫画合集。 */
export const isCollection = (category?: number): boolean =>
  category === MANGA_CATEGORY_CONFIG.COLLECTION.value

/** 判断漫画类别是否为百合动画。 */
export const isAnimation = (category?: number): boolean =>
  category === MANGA_CATEGORY_CONFIG.YURI_ANIMATION.value

/** 页面筛选与详情展示使用的漫画状态列表。 */
export const MANGA_STATUS: MangaConfig[] = Object.values(MANGA_STATUS_CONFIG)

/** 页面筛选与详情展示使用的漫画类别列表。 */
export const MANGA_CATEGORY: MangaConfig[] = Object.values(
  MANGA_CATEGORY_CONFIG
)

/** 百合动画类别 ID，用于动画类稿件的岗位逻辑分支。 */
export const ANIME_CATEGORY_ID: number =
  MANGA_CATEGORY_CONFIG.YURI_ANIMATION.value
